import React from 'react';
import { ArrowRight, Play, Code, Cloud, Settings, Users } from 'lucide-react';
import { companyData } from '../data/content';

const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="home" className="min-h-screen flex items-center relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 gradient-bg opacity-10"></div>
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary-300/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-coral-300/20 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-6 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 px-4 py-2 rounded-full text-sm font-medium">
                <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                <span>Established 2019</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                <span className="block text-gray-900 dark:text-white">
                  {companyData.name}
                </span>
                <span className="block bg-gradient-to-r from-primary-500 to-coral-500 bg-clip-text text-transparent">
                  {companyData.tagline}
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl">
                Empowering businesses across Tanzania with innovative software solutions, 
                cloud computing, and comprehensive system support since 2019.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollToSection('#services')}
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-primary-500 to-coral-500 text-white rounded-lg font-semibold hover:from-primary-600 hover:to-coral-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Explore Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              
              <button
                onClick={() => scrollToSection('#about')}
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-500 text-primary-500 rounded-lg font-semibold hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-all duration-300"
              >
                <Play className="mr-2 w-5 h-5" />
                Learn More
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 pt-8">
              {companyData.stats.map((stat, index) => (
                <div key={index} className="text-center lg:text-left">
                  <div className="text-3xl font-bold text-primary-500 dark:text-primary-400">
                    {stat.number}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Visual Elements */}
          <div className="relative">
            <div className="relative z-10">
              {/* Service Icons Grid */}
              <div className="grid grid-cols-2 gap-6">
                <div className="glass-effect rounded-2xl p-8 text-center space-y-4 transform hover:scale-105 transition-transform duration-300 h-64 flex flex-col justify-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-coral-500 rounded-xl flex items-center justify-center mx-auto">
                    <Code className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">System Development</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Custom Software Solutions</p>
                </div>
                
                <div className="glass-effect rounded-2xl p-8 text-center space-y-4 transform hover:scale-105 transition-transform duration-300 h-64 flex flex-col justify-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-coral-500 to-primary-500 rounded-xl flex items-center justify-center mx-auto">
                    <Cloud className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Cloud Computing</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Scalable Infrastructure</p>
                </div>
                
                <div className="glass-effect rounded-2xl p-8 text-center space-y-4 transform hover:scale-105 transition-transform duration-300 h-64 flex flex-col justify-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-coral-500 rounded-xl flex items-center justify-center mx-auto">
                    <Settings className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">System Support</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">24/7 Technical Support</p>
                </div>
                
                <div className="glass-effect rounded-2xl p-8 text-center space-y-4 transform hover:scale-105 transition-transform duration-300 h-64 flex flex-col justify-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-coral-500 to-primary-500 rounded-xl flex items-center justify-center mx-auto">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Consultation</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Business Advisory</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;